/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/audit-search-box.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useQuery } from '@apollo/client/react';
import moment from 'moment';
import React, { useState, useEffect, useMemo } from 'react';
import Areas from './definitions/areas';
import TypeAreas from './definitions/type-areas';
import { getSearchConditions } from './service';
import { useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_ALL_USER_LIST } from 'queries/uam';
import { AuditApplicationArea, AuditSearchOperator } from 'types/graphql-global-types';
import { SaveButton } from 'views/components/core/buttons';
import DatePicker from 'views/components/core/date-picker';
import CustomMultiSelect from 'views/components/core/multi-select/multi-select';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import 'views/pages/admin/admin.scss';
import 'views/pages/admin/audit-log/audit-log.scss';

export default function ({ onSearch, selectedOrganisation, userStudies }: any) {
    const { t } = useTranslation();

    const initialState = {
        studies: [] as any,
        area: Areas.find((area: any) => area.type === AuditApplicationArea.OPRA),
        type: '',
        startDate: moment().subtract(5, 'day').toDate(),
        endDate: null as any,
        byEmail: '',
        byFirst: '',
        byLast: '',
        action: '',
        reason: '',
        name: '',
        recordId: '',
        userId: null as any,
        conjunction: AuditSearchOperator.AND,
        fields: [] as any,
    };

    const [state, updateState] = useState(() => initialState);
    const { data, loading } = useQuery(GET_ALL_USER_LIST);

    useEffect(() => {
        if (JSON.stringify(initialState) !== JSON.stringify(state)) {
            updateState({ ...initialState });
        }
    }, [selectedOrganisation]);

    const getTypes = () =>
        state.area?.type === AuditApplicationArea.OPRA
            ? TypeAreas
            : TypeAreas.filter((type: any) => type.areas.indexOf(state.area) >= 0);

    const typesOptions = [{ id: '', text: 'ALL' }].concat(
        getTypes()
            .map((type: any) => ({
                id: type.field,
                text: t(`auditLog:${type.type}`),
            }))
            .sort((a: any, b: any) => a.text.localeCompare(b.text)),
    );

    const actionsOptions = [
        {
            id: '',
            text: t('common:none'),
        },
        {
            id: 'Add',
            text: t('uam:add'),
        },
        {
            id: 'Delete',
            text: t('uam:delete'),
        },
        {
            id: 'Update',
            text: t('uam:update'),
        },
        {
            id: 'Restore',
            text: t('uam:restore'),
        },
        {
            id: 'DataExport',
            text: t('auditLog:dataExport'),
        },
    ];

    const onChange = (name: string) => (event: any) => {
        const { value } = event.target;

        if (name === 'areaType') {
            updateState({
                ...state,
                area: {
                    ...state.area,
                    name: state.area?.name || '',
                    type: event.target?.value,
                },
            });
        } else {
            updateState({
                ...state,
                [name]: value,
            });
        }
    };

    const myStudies = useMemo(
        () =>
            (userStudies ?? []).map((study: StudyFields) => ({
                id: study.id,
                name: study.studyNumber,
            })),
        [userStudies],
    );

    return (
        <div className="log-wrap">
            <div className="log-fields-group-wrap">
                <div className="log-field-wrap log-select-wrap multi-select">
                    <CustomMultiSelect
                        items={myStudies.filter(
                            (myStudy: any) => state.studies.findIndex((study: any) => myStudy.name === study.name) < 0,
                        )}
                        onChange={(value: any) => {
                            if (value.includes(undefined)) return;

                            updateState({
                                ...state,
                                studies: [...value],
                            });
                        }}
                        value={state.studies}
                        label="Studies"
                        customClassName="form-action-type audit-log-studies"
                        customId="audit-studies"
                        placeholder={t('admin:selectStudies')}
                    />
                </div>
                <div className="log-field-wrap log-select-wrap">
                    <SelectWithCategories
                        showWarning={false}
                        customClassName="log-select"
                        fieldSelect="type"
                        onChange={onChange}
                        label={t('admin:entityType')}
                        filterable={false}
                        labelField="type"
                        dataArray={typesOptions}
                        customId="select-entityType"
                        currentState={state.type || ''}
                    />
                </div>
            </div>

            <div className="log-fields-group-wrap">
                <span className="date-label">{t('admin:between')}:</span>
                <div id="log-start-date" className="date-field-wrap">
                    <DatePicker
                        label="Start Date"
                        customId="start-date"
                        name="startDate"
                        labelWidth={135}
                        selectedDate={state.startDate}
                        handleDateChange={onChange('startDate')}
                    />
                </div>
                <span className="date-label">{t('admin:and')}</span>
                <div id="log-end-date" className="date-field-wrap">
                    <DatePicker
                        label="End Date"
                        customId="end-date"
                        name="endDate"
                        labelWidth={135}
                        selectedDate={state.endDate || new Date()}
                        handleDateChange={onChange('endDate')}
                    />
                </div>
            </div>

            <div className="log-fields-group-wrap">
                <span className="date-label">{t('admin:byUser')}:</span>
                <div className="log-field-wrap log-select-wrap">
                    {!loading && (
                        <SelectWithCategories
                            showWarning={false}
                            customClassName="log-select"
                            fieldSelect="userId"
                            onChange={onChange}
                            label={t('admin:user')}
                            filterable={false}
                            labelField="Member"
                            dataArray={[{ id: '', text: t('common:none') }].concat(
                                data?.users
                                    ?.map((u: any) => ({ id: u.id, text: `${u.firstName} ${u.lastName}` }))
                                    ?.sort((a: any, b: any) => a.text.localeCompare(b.text)),
                            )}
                            customId="select-user"
                            currentState={state.userId || ''}
                        />
                    )}
                </div>
                <TextField
                    id="audit-by-email"
                    className="log-field-wrap"
                    name="byEmail"
                    value={state.byEmail}
                    onChange={onChange('byEmail')}
                    label="Email"
                    variant="outlined"
                />
                <TextField
                    id="audit-by-first"
                    className="log-field-wrap"
                    name="byFirst"
                    value={state.byFirst}
                    onChange={onChange('byFirst')}
                    label="First Name"
                    variant="outlined"
                />
                <TextField
                    id="audit-by-last"
                    className="log-field-wrap"
                    name="byLast"
                    value={state.byLast}
                    onChange={onChange('byLast')}
                    label="Last Name"
                    variant="outlined"
                />
            </div>

            <div className="log-fields-group-wrap">
                <div className="log-field-wrap log-select-wrap">
                    <SelectWithCategories
                        showWarning={false}
                        customClassName="log-select"
                        fieldSelect="action"
                        onChange={onChange}
                        label={t('ra:action')}
                        filterable={false}
                        labelField="action"
                        dataArray={actionsOptions}
                        customId="select-action-type"
                        currentState={state.action || 'NONE'}
                    />
                </div>

                <TextField
                    id="audit-by-reason"
                    className="log-field-wrap"
                    name="reason"
                    value={state.reason}
                    onChange={onChange('reason')}
                    label="Reason"
                    variant="outlined"
                />

                <TextField
                    id="audit-by-item-name"
                    className="log-field-wrap"
                    name="name"
                    value={state.name}
                    onChange={onChange('name')}
                    label="Name"
                    variant="outlined"
                />
                <TextField
                    id="audit-by-id"
                    className="log-field-wrap"
                    name="recordId"
                    value={state.recordId}
                    onChange={onChange('recordId')}
                    label="Item ID"
                    variant="outlined"
                />
            </div>
            <div className="search-button-wrap">
                <SaveButton
                    text={t('common:clear')}
                    onClick={() => {
                        updateState({ ...initialState });
                        const initialChanges = getSearchConditions(initialState);
                        const newChanges = getSearchConditions(state);
                        const isChangesApplied = JSON.stringify(initialChanges) !== JSON.stringify(newChanges);
                        onSearch(initialState, isChangesApplied);
                    }}
                    style={{ marginRight: 10 }}
                />
                <SaveButton
                    text={t('common:search')}
                    onClick={() => {
                        const initialChanges = getSearchConditions(initialState);
                        const newChanges = getSearchConditions(state);
                        const isChangesApplied = JSON.stringify(initialChanges) !== JSON.stringify(newChanges);
                        onSearch(state, isChangesApplied);
                    }}
                />
            </div>
        </div>
    );
}
